import { creditPacks } from "./credits";
import { config } from "./index";

/**
 * Creem 时代的产品 ID → 档位与周期。
 *
 * 为什么需要这张表：NEXT_PUBLIC_PRODUCT_ID_* 换成 Waffo 的 PROD_xxx 之后，
 * 存量 Creem 订阅续费时 webhook 带回来的仍然是老的 prod_xxx。
 * determinePlanFromProductId 在 config.payments.plans 里找不到它，会返回
 * { planType: "free" } —— 于是一个正在正常付费的用户，会在续费成功的那一刻
 * 被降级成免费档。这张表就是拦住这件事的唯一一道闸。
 *
 * 映射到「档位 + 周期」而不是直接映射到新的 Waffo ID，这样 env 里的产品 ID
 * 以后再变也不用回来改这张表。
 *
 * ⚠️ 切 env 之前必须先跑 packages/scripts/audit-legacy-product-ids.ts，
 * 用生产库里 Purchase / CreditGrant 实际出现过的 productId 校验这张表是否完整。
 */
export const CREEM_LEGACY_PRODUCT_IDS: Record<
	string,
	{ plan: "starter" | "pro"; interval: "month" | "year" }
> = {
	// Creem 生产目录里实际存在的四个在售订阅产品（2026-09-01 拉取 Creem API 所得）。
	// 2026-09-02 核过生产库：只有这四个 ID 出现在 Purchase 上，共 5 个生效订阅。
	prod_5gv7sEJArUxxt7oqmXygOW: { plan: "starter", interval: "month" }, // $9.90
	prod_61PGec5uC4xuFJ510c9rmE: { plan: "starter", interval: "year" }, // $71
	prod_2nepGMHVJMNscFipSCfvYJ: { plan: "pro", interval: "month" }, // $19.90
	prod_6PMABwGOcLRIKQ4nL7HGNh: { plan: "pro", interval: "year" }, // $143

	// Creem 测试环境的三个订阅产品。生产库里 0 命中（2026-09-02 用 Neon 连接串跑过
	// audit-legacy-product-ids），但本地开发库上挂着 8 个生效订阅——删掉就没法在本地
	// 预演 env 切换了。
	prod_3W01Q53JOL28AtC9upBYok: { plan: "pro", interval: "month" },
	prod_3qF5B6hcCBhSwqTrrPFeeq: { plan: "starter", interval: "month" },
	prod_6Gza95JlEprbe5JH64Y31Z: { plan: "pro", interval: "year" },
};

/**
 * Creem 时代的一次性积分包产品 ID → 当前 creditPacks 里的包 id。
 *
 * 为什么单独一张表：去水印是买断的永久权益，helper.ts 的 canRemoveWatermark 靠
 * getCreditPackByProductId(purchase.productId) 判定。env 换成 Waffo 的 PROD_xxx
 * 之后，历史 ONE_TIME 行上的 prod_xxx 查不到任何包，只买过积分包的用户会在切换
 * 那一刻永久失去去水印权限。订阅那条线有 CREEM_LEGACY_PRODUCT_IDS 兜着，积分包
 * 进不了那张表（它的值是「档位 + 周期」，积分包两者都没有）。
 *
 * 映射到包 id 而不是新的 Waffo 产品 ID，理由同上：env 里的 ID 以后再变也不用回来改。
 *
 * 2026-09-02 用生产库核过：这两个 ID 上有 15 行 ONE_TIME、涉及 10 个用户。
 */
export const CREEM_LEGACY_CREDIT_PACK_IDS: Record<string, string> = {
	// Creem 生产
	prod_6OYBx4fjptIPAWUZfQskxl: "pack_small", // $6.90 / 120 credits
	prod_OxZNaW5b7rnc9vlqJhKJQ: "pack_large", // $16.90 / 320 credits

	// Creem 测试。与上面那三个测试订阅 ID 同理：生产库 0 命中，但本地开发库上
	// 各有一笔一次性购买，留着才能在本地把去水印这条路径也一起预演。
	prod_O4iWq7klt1N4mRYASysVU: "pack_small", // 当时叫 80 Credits Pack
	prod_2RK48aXCkmf0bJMsSbYa0Q: "pack_large", // 当时叫 220 Credits Pack
};

/**
 * 把可能是 Creem 时代的产品 ID 翻译成当前 env 配置的产品 ID。
 * 不是遗留 ID 就原样返回，所以可以无脑套在所有 provider 的入口上。
 */
export function toCurrentProductId(productId: string): string {
	const legacyPackId = CREEM_LEGACY_CREDIT_PACK_IDS[productId];

	if (legacyPackId) {
		const currentPack = creditPacks.find(
			(pack) => pack.id === legacyPackId,
		);

		return currentPack?.productId || productId;
	}

	const legacy = CREEM_LEGACY_PRODUCT_IDS[productId];

	if (!legacy) {
		return productId;
	}

	const plan = config.payments.plans[
		legacy.plan as keyof typeof config.payments.plans
	] as { prices?: { productId: string; interval?: string }[] } | undefined;

	const current = plan?.prices?.find(
		(price) => price.interval === legacy.interval,
	)?.productId;

	return current || productId;
}

/**
 * 认出「长得像 Creem 产品 ID 但不在映射表里」的情况。命中就说明有付费用户会被
 * 悄悄降级，必须让它在日志里喊出来，而不是安静地当成 free。
 */
export function isUnmappedLegacyProductId(productId: string): boolean {
	if (!productId.startsWith("prod_")) {
		return false;
	}

	if (
		CREEM_LEGACY_PRODUCT_IDS[productId] ||
		CREEM_LEGACY_CREDIT_PACK_IDS[productId]
	) {
		return false;
	}

	// 当前 env 里配的产品 ID 本身就叫 prod_（还没切到 Waffo）时不算异常。
	// 积分包也要算进去：代码先部署、env 后切的窗口期里，Creem 的积分包订单
	// 不能被当成「认不出」拒收，否则积分发不出去。
	const configuredInPlans = Object.values(config.payments.plans).some(
		(plan) =>
			"prices" in plan
				? plan.prices?.some((price) => price.productId === productId)
				: false,
	);
	const configuredInPacks = creditPacks.some(
		(pack) => pack.productId === productId,
	);

	return !configuredInPlans && !configuredInPacks;
}
